import { useEffect, useRef, useState } from "react";
import { LinearProgress } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Drawer from "../../components/ui/Drawer";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { useCreatePackageMutation } from "../../api/libraryApi";
import { SoftButton, SoftTag, cls } from "../hatim/soft/SoftKit";
import { downloadLibraryTemplate, parseLibraryWorkbook } from "../../lib/libraryExcel";
import { TYPE_LABEL, toPayload } from "../muzakere/questionRules";

/**
 * Sorulu müzakere paketlerini Excel'den toplu yükleme çekmecesi.
 * Örnek dosya indirilir, doldurulan dosya seçilir; dosya tarayıcıda
 * doğrulanır (ya hep ya hiç). Geçerliyse paketler önizlenir ve sırayla
 * oluşturulur; her paket kendi isteğiyle gider, sonuç tek tek gösterilir.
 */
export default function LibraryImportDrawer({ open, onClose }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const inputRef = useRef(null);
  const [createPackage] = useCreatePackageMutation();
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null); // { packages, errors }
  const [submit, setSubmit] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState({}); // index → "ok" | hata metni

  useEffect(() => {
    if (!open) return;
    setFileName("");
    setParsed(null);
    setSubmit(false);
    setResults({});
  }, [open]);

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setFileName(file.name);
    setResults({});
    const buffer = await file.arrayBuffer();
    setParsed(parseLibraryWorkbook(new Uint8Array(buffer)));
  };

  const packages = parsed?.packages || [];
  const errors = parsed?.errors || [];
  const done = Object.keys(results).length;
  const okCount = Object.values(results).filter((r) => r === "ok").length;
  // Hepsi oluştuysa bitti; başarısız kalan varsa "Yükle" yalnız onları yeniden dener.
  const finished = packages.length > 0 && okCount === packages.length;
  const failedCount = done - okCount;

  const upload = async () => {
    setRunning(true);
    const next = {};
    for (let i = 0; i < packages.length; i += 1) {
      if (results[i] === "ok") {
        next[i] = "ok";
        continue;
      }
      const pkg = packages[i];
      const res = await createPackage({
        title: pkg.title,
        description: pkg.description || undefined,
        tags: pkg.tags.length ? pkg.tags : undefined,
        questions: pkg.questions.map(toPayload),
        submit,
      });
      next[i] = res.error ? res.error.message || t("lib_import_failed_one") : "ok";
      setResults({ ...next });
    }
    setRunning(false);
    const ok = Object.values(next).filter((r) => r === "ok").length;
    if (ok === packages.length) toast.success(t("lib_import_done", { count: ok }));
    else toast.error(t("lib_import_partial", { ok, total: packages.length }));
  };

  const footer = (
    <div className="flex gap-2">
      <SoftButton tone="soft" fullWidth onClick={onClose} disabled={running}>
        {finished ? t("manage_goals_close") : t("group_join_cancel")}
      </SoftButton>
      {!finished ? (
        <SoftButton
          tone="primary"
          fullWidth
          startIcon={<UploadFileOutlinedIcon />}
          disabled={running || packages.length === 0}
          onClick={upload}
        >
          {failedCount > 0 && !running
            ? t("lib_import_retry", { count: failedCount })
            : packages.length
              ? t("lib_import_upload", { count: packages.length })
              : t("lib_import_upload_empty")}
        </SoftButton>
      ) : null}
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={running ? undefined : onClose}
      width="md"
      title={t("lib_import_title")}
      subtitle={t("lib_import_subtitle")}
      titleIcon={<UploadFileOutlinedIcon />}
      footer={footer}
    >
      <div className="tk-dark flex flex-col">
        {/* 1. Örnek dosya */}
        <section className={`px-5 py-4 flex flex-col gap-2 ${cls.row}`}>
          <div className={`text-md font-SemiBold ${cls.title}`}>{t("lib_import_step_template")}</div>
          <p className={`text-base ${cls.muted}`}>{t("lib_import_template_hint")}</p>
          <div>
            <SoftButton tone="soft" startIcon={<DownloadIcon />} onClick={downloadLibraryTemplate}>
              {t("lib_import_download_template")}
            </SoftButton>
          </div>
        </section>

        {/* 2. Dosya seçimi */}
        <section className={`px-5 py-4 flex flex-col gap-2 ${cls.row}`}>
          <div className={`text-md font-SemiBold ${cls.title}`}>{t("lib_import_step_file")}</div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={onFile}
            data-testid="library-import-input"
          />
          <button
            type="button"
            disabled={running}
            onClick={() => inputRef.current?.click()}
            className="w-full rounded-2xl border-2 border-dashed border-terakkiBrandMist dark:border-white/20 px-4 py-6 flex flex-col items-center gap-1.5 text-center hover:bg-terakkiBrandCloud dark:hover:bg-white/5 transition-colors"
          >
            <UploadFileOutlinedIcon className={cls.accent} />
            <span className={`text-base ${cls.title}`}>{fileName || t("lib_import_pick_file")}</span>
            <span className={`text-sm ${cls.muted}`}>.xlsx</span>
          </button>
        </section>

        {/* 3. Sonuç: hatalar ya da önizleme */}
        {errors.length ? (
          <section className="px-5 py-4 flex flex-col gap-2" data-testid="library-import-errors">
            <div className={`flex items-center gap-2 text-md font-SemiBold ${cls.danger}`}>
              <ErrorOutlineIcon fontSize="small" aria-hidden />
              {t("lib_import_errors_title", { count: errors.length })}
            </div>
            <p className={`text-base ${cls.muted}`}>{t("lib_import_errors_hint")}</p>
            <ul className="flex flex-col gap-1.5 max-h-[320px] overflow-y-auto">
              {errors.map((e) => (
                <li key={e} className={`text-base ${cls.title}`}>
                  {e}
                </li>
              ))}
            </ul>
          </section>
        ) : packages.length ? (
          <section className="px-5 py-4 flex flex-col gap-3">
            <div className={`text-md font-SemiBold ${cls.title}`}>
              {t("lib_import_preview_title", { count: packages.length })}
            </div>
            <label className={`flex items-center gap-2.5 text-base cursor-pointer ${cls.title}`}>
              <input
                type="checkbox"
                checked={submit}
                disabled={running || done > 0}
                onChange={(e) => setSubmit(e.target.checked)}
                className="accent-terakkiPrimaryDark w-4 h-4"
              />
              {t("lib_import_submit_review")}
            </label>
            {running ? (
              <LinearProgress
                variant="determinate"
                value={Math.round((done / packages.length) * 100)}
                className="!h-1.5 !rounded-full"
              />
            ) : null}
            <ul className="flex flex-col divide-y divide-terakkiBorder">
              {packages.map((pkg, i) => {
                const result = results[i];
                const types = [...new Set(pkg.questions.map((q) => t(TYPE_LABEL[q.type])))].join(", ");
                return (
                  <li key={pkg.title} className="py-3 flex items-start gap-3">
                    <span className="mt-0.5 shrink-0">
                      {result === "ok" ? (
                        <CheckCircleOutlineIcon fontSize="small" className="text-[#2E9E6A]" aria-hidden />
                      ) : result ? (
                        <ErrorOutlineIcon fontSize="small" className={cls.danger} aria-hidden />
                      ) : (
                        <span className={`block w-5 h-5 rounded-full border-2 ${running && i === done ? "border-terakkiPrimary animate-pulse" : "border-terakkiBrandMist dark:border-white/20"}`} />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className={`text-md break-words ${cls.title}`}>{pkg.title}</div>
                      <div className={`text-sm ${cls.muted}`}>
                        {[`${pkg.questions.length} ${t("lib_word_question")}`, types, pkg.tags.join(", ")]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                      {result && result !== "ok" ? <div className={`text-sm ${cls.danger}`}>{result}</div> : null}
                    </div>
                    {result === "ok" ? <SoftTag>{t(submit ? "lib_import_sent_review" : "lib_import_created")}</SoftTag> : null}
                  </li>
                );
              })}
            </ul>
            {done === packages.length && !running ? (
              <p className={`text-base ${cls.muted}`}>{t("lib_import_summary", { ok: okCount, total: packages.length })}</p>
            ) : null}
          </section>
        ) : null}
      </div>
    </Drawer>
  );
}
